import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiFileText,
  FiCopy,
  FiDollarSign,
  FiClock,
  FiPackage,
  FiBarChart2,
  FiColumns,
  FiPrinter,
  FiArrowLeft,
  FiUser
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { formulacionesService, productosService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';

const Formulaciones = () => {
  const navigate = useNavigate();
  const [todasFormulaciones, setTodasFormulaciones] = useState([]); // Todas las fórmulas
  const [formulaciones, setFormulaciones] = useState([]); // Fórmulas filtradas
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('naturalandlab');
  const [error, setError] = useState(null);

  // Estado para vista de clientes NaturalAndLab
  const [naturalandlabProducts, setNaturalandlabProducts] = useState([]); // Productos NATURALANDLAB con cliente asignado
  const [selectedNaturalAndLabClient, setSelectedNaturalAndLabClient] = useState(null); // Cliente seleccionado

  // Aplicar debounce al searchQuery
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // ========== FUNCIONES useCallback (deben estar ANTES de los useEffect) ==========

  const transformFormulacionFromAPI = useCallback((formula) => ({
    id: formula.id,
    nombreProducto: formula.formula_name || '',
    compania: formula.company_name || '',
    tipoFormula: formula.formula_type || 'betel',
    departamento: formula.product_type || '', // Campo departamento desde product_type
    tipoEnvase: formula.container_product_name || '',
    cantidadPorEnvase: formula.quantity_per_container || 0,
    unidad: formula.quantity_unit || 'UNIT',
    tipoCapsulas: formula.capsule_type || '',
    tiempoMezcla: formula.mixing_time_minutes || 0,
    fechaMejorUso: formula.best_use_date_years || 0,
    incluyeSilica: formula.includes_silica || false,
    costoSilica: formula.silica_cost || 0,
    ingredientes: formula.ingredients || [],
    cantidadIngredientes: formula.total_ingredients || (formula.ingredients || []).length,
    notas: formula.notes || '',
    fechaCreacion: formula.created_at || '',
    fechaActualizacion: formula.updated_at || '',
    estado: formula.is_active ? 'activa' : 'inactiva',
    resultProductId: formula.result_product_id || null, // ID del producto resultado
  }), []);

  // Filtrar fórmulas según el tab activo (por departamento/product_type)
  const filtrarFormulacionesPorTipo = useCallback((formulas, tipo) => {
    let filtradas = formulas;

    if (tipo === 'cotizaciones') {
      filtradas = formulas.filter(f => f.departamento === 'COTIZACIONES');
    } else if (tipo === 'betel') {
      filtradas = formulas.filter(f => f.departamento === 'BETEL');
    } else if (tipo === 'naturalandlab') {
      filtradas = formulas.filter(f => f.departamento === 'NATURALANDLAB');
    }

    setFormulaciones(filtradas);
  }, []);

  // Cargar TODAS las fórmulas (sin filtro de tipo)
  const loadTodasFormulaciones = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const baseParams = {
        skip: 0,
        limit: 100, // Límite máximo permitido por la API
        is_active: true
      };

      const result = await formulacionesService.getFormulaciones(baseParams);

      if (result.success) {
        const formulacionesTransformadas = result.data.map(transformFormulacionFromAPI);
        setTodasFormulaciones(formulacionesTransformadas);
        // Filtrar inmediatamente según el tab activo
        filtrarFormulacionesPorTipo(formulacionesTransformadas, activeTab);
      } else {
        setError(result.message || 'Error al cargar formulaciones');
        setTodasFormulaciones([]);
        setFormulaciones([]);
      }
    } catch (error) {
      setError('Error de conexión al cargar formulaciones');
      console.error('Error al cargar formulaciones:', error);
      setTodasFormulaciones([]);
      setFormulaciones([]);
    } finally {
      setIsLoading(false);
    }
  }, [transformFormulacionFromAPI, activeTab, filtrarFormulacionesPorTipo]);

  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      // Si no hay búsqueda, mostrar fórmulas filtradas del tab activo
      filtrarFormulacionesPorTipo(todasFormulaciones, activeTab);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchParams = {
        skip: 0,
        limit: 100,
        is_active: activeTab !== 'inactivas',
        search: query
      };

      // NO filtrar por formula_type al buscar, porque las fórmulas pueden tener
      // formula_type=BETEL pero product_type=NATURALANDLAB
      // El filtrado por departamento se hace después en el frontend

      const result = await formulacionesService.getFormulaciones(searchParams);

      if (result.success) {
        // Transformar y filtrar por departamento (product_type) en el frontend
        const formulacionesData = result.data.map(transformFormulacionFromAPI);

        // Filtrar por el tab activo usando el departamento (product_type)
        const formulacionesFiltradas = formulacionesData.filter(f => {
          if (activeTab === 'cotizaciones') return f.departamento === 'COTIZACIONES';
          if (activeTab === 'betel') return f.departamento === 'BETEL';
          if (activeTab === 'naturalandlab') return f.departamento === 'NATURALANDLAB';
          return true;
        });

        setFormulaciones(formulacionesFiltradas);
      } else {
        setError(result.message || 'Error en la búsqueda');
        setFormulaciones([]);
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching formulaciones:', err);
      setFormulaciones([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, todasFormulaciones, transformFormulacionFromAPI, filtrarFormulacionesPorTipo]);

  // Calcular totales por pestaña (usando TODAS las fórmulas)
  const getTotalFormulaciones = useCallback((tipo) => {
    return todasFormulaciones.filter(f => {
      if (tipo === 'cotizaciones') return f.departamento === 'COTIZACIONES';
      if (tipo === 'betel') return f.departamento === 'BETEL';
      if (tipo === 'naturalandlab') return f.departamento === 'NATURALANDLAB';
      return true;
    }).length;
  }, [todasFormulaciones]);

  // ========== EFECTOS useEffect ==========

  // Cargar todas las fórmulas al inicio
  useEffect(() => {
    loadTodasFormulaciones();
  }, [loadTodasFormulaciones]);

  // Filtrar fórmulas cuando cambia el tab
  useEffect(() => {
    if (todasFormulaciones.length > 0 && !searchQuery) {
      filtrarFormulacionesPorTipo(todasFormulaciones, activeTab);
    }
  }, [activeTab, todasFormulaciones, searchQuery, filtrarFormulacionesPorTipo]);

  // Realizar búsqueda cuando cambia el query
  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, performSearch]);

  // Cargar productos NATURALANDLAB para la vista por cliente
  useEffect(() => {
    const loadNaturalandlabProducts = async () => {
      try {
        const result = await productosService.getProductos({
          product_type: 'NATURALANDLAB',
          limit: 100,
          skip: 0
        });
        if (result.success) {
          const data = result.data || [];
          // Solo productos con cliente asignado
          setNaturalandlabProducts(data.filter(p => p.naturalandlab_client_id));
        }
      } catch (err) {
        console.error('Error cargando productos NaturalAndLab:', err);
      }
    };
    loadNaturalandlabProducts();
  }, []);

  // Resetear cliente seleccionado al cambiar de tab
  useEffect(() => {
    setSelectedNaturalAndLabClient(null);
  }, [activeTab]);

  // ========== HANDLERS ==========

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearError = () => {
    setError(null);
    loadTodasFormulaciones();
  };

  // Obtener título según tab activo
  const getTituloFormulaciones = () => {
    switch (activeTab) {
      case 'naturalandlab':
        return 'FORMULACIONES - NATURALANDLAB';
      case 'cotizaciones':
        return 'FORMULACIONES - COTIZACIONES';
      case 'betel':
        return 'FORMULACIONES - BETEL';
      default:
        return 'FORMULACIONES';
    }
  };

  const handleDuplicar = async (formulacion) => {
    try {
      const result = await formulacionesService.duplicarFormulacion(formulacion.id, {
        new_company_name: formulacion.compania,
        new_formula_name: `${formulacion.nombreProducto} (Copia)`,
        copy_ingredients: true,
        copy_production_quantities: true
      });

      if (result.success) {
        navigate(`/system/formulaciones/${result.data.id}`);
      }
    } catch (error) {
      console.error('Error al duplicar formulación:', error);
    }
  };

  const handleImprimir = async (formulacion) => {
    try {
      console.log('Imprimiendo formulación:', formulacion);

      // Obtener los detalles completos de la fórmula
      const detalleResult = await formulacionesService.getFormulacion(formulacion.id);
      console.log('Resultado detalle:', detalleResult);

      if (!detalleResult.success) {
        console.error('Error al cargar detalles de la fórmula:', detalleResult);
        alert('Error al cargar los detalles de la fórmula');
        return;
      }

      const formulaDetalle = detalleResult.data;
      console.log('Formula detalle cargada:', formulaDetalle);

      // Generar el cálculo de estimado
      const calculoResult = await formulacionesService.calcularCostos(formulacion.id, {
        production_quantity: 1
      });
      console.log('Resultado cálculo:', calculoResult);

      if (!calculoResult.success) {
        console.error('Error al calcular costos:', calculoResult);
        alert('Error al calcular los costos de la fórmula');
        return;
      }

      const calculo = calculoResult.data;
      console.log('Calculo obtenido:', calculo);

      generarTirillaFormula(formulaDetalle, calculo);
    } catch (error) {
      console.error('Error al generar impresión:', error);
      alert('Error al generar la impresión: ' + error.message);
    }
  };

  const fmtProdQty = (total, unit) => {
    const u = (unit || '').toUpperCase();
    switch (u) {
      case 'KG': return `${total.toFixed(3)} kg`;
      case 'G':
        if (total >= 1000) return `${(total / 1000).toFixed(3)} kg`;
        return `${total.toFixed(2)} g`;
      case 'MG': {
        const g = total / 1000;
        if (g >= 1000) return `${(g / 1000).toFixed(3)} kg`;
        if (g >= 1) return `${g.toFixed(2)} g`;
        return `${total.toFixed(0)} mg`;
      }
      case 'MCG': {
        const mg = total / 1000;
        const g = total / 1000000;
        if (g >= 1000) return `${(g / 1000).toFixed(3)} kg`;
        if (g >= 1) return `${g.toFixed(3)} g`;
        if (mg >= 1) return `${mg.toFixed(2)} mg`;
        return `${total.toFixed(0)} mcg`;
      }
      case 'L': return `${total.toFixed(3)} l`;
      case 'ML':
        if (total >= 1000) return `${(total / 1000).toFixed(3)} l`;
        return `${total.toFixed(2)} ml`;
      case 'UNIT': return `${Math.round(total)} unid`;
      default: return `${total.toFixed(2)} ${u.toLowerCase()}`;
    }
  };

  const generarTirillaFormula = (formula, calculo) => {
    const prodQtys = [250, 500, 1000, 2000];
    const ingredients = (formula.ingredients || []).filter(i => !i.isNew);
    const costMap = {};
    (calculo.ingredient_costs || []).forEach(c => { costMap[c.ingredient_name] = c; });

    const tirillaHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Fórmula - ${formula.formula_name || 'Sin nombre'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }

          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 900px;
            margin: 0 auto;
            font-size: 12px;
            line-height: 1.4;
          }

          .header {
            text-align: center;
            margin-bottom: 16px;
            border-bottom: 2px solid #7B2D8E;
            padding-bottom: 12px;
          }
          .logo { font-size: 20px; font-weight: bold; color: #7B2D8E; }
          .title { font-size: 15px; font-weight: bold; margin: 6px 0; text-transform: uppercase; }

          .info-section {
            display: flex;
            flex-wrap: wrap;
            gap: 6px 24px;
            margin-bottom: 14px;
            padding-bottom: 10px;
            border-bottom: 1px solid #ddd;
            font-size: 12px;
          }
          .info-row { display: flex; gap: 6px; }
          .label { font-weight: bold; }

          table {
            width: 100%;
            border-collapse: collapse;
            margin: 14px 0;
            font-size: 11px;
          }
          thead tr:first-child th {
            background: #7B2D8E;
            color: #fff;
            padding: 7px 6px;
            text-align: center;
            border: 1px solid #5a2068;
          }
          thead tr:last-child th {
            background: #f3e8f7;
            color: #5a2068;
            padding: 5px 6px;
            text-align: center;
            border: 1px solid #ddd;
            font-weight: bold;
          }
          td {
            padding: 5px 6px;
            border: 1px solid #e0e0e0;
            vertical-align: middle;
          }
          tr:nth-child(even) td { background: #fafafa; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .base-unit { font-size: 10px; color: #999; }

          tfoot td {
            background: #f3e8f7 !important;
            font-weight: bold;
            border-top: 2px solid #7B2D8E;
          }

          .costs-section {
            margin-top: 16px;
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: hidden;
          }
          .costs-title {
            background: #7B2D8E;
            color: #fff;
            padding: 6px 10px;
            font-weight: bold;
            font-size: 12px;
          }
          .cost-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 10px;
            border-bottom: 1px solid #f0f0f0;
            font-size: 12px;
          }
          .cost-row.grand-total {
            background: #f3e8f7;
            font-weight: bold;
            font-size: 13px;
            border-top: 2px solid #7B2D8E;
          }

          .footer {
            margin-top: 16px;
            padding-top: 10px;
            border-top: 1px dashed #ccc;
            text-align: center;
            font-size: 10px;
            color: #999;
          }

          @media print {
            body { padding: 10px; }
            @page { margin: 10mm; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">BETEL NATURAL</div>
          <div class="title">FÓRMULA — ${formula.formula_name || 'Sin nombre'}</div>
          ${formula.company_name ? `<div style="font-size:13px;">${formula.company_name}</div>` : ''}
          <div style="font-size:11px;margin-top:4px;">
            Fecha: ${new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="label">Tipo:</span>
            <span>${translateFormulaType(formula.formula_type)}</span>
          </div>
          <div class="info-row">
            <span class="label">Envase:</span>
            <span>${formula.container_product_name || 'No asignado'}</span>
          </div>
          <div class="info-row">
            <span class="label">Cant. por Envase:</span>
            <span>${formula.quantity_per_container || 0} ${translateUnit(formula.quantity_unit)}</span>
          </div>
          ${formula.capsule_type ? `
          <div class="info-row">
            <span class="label">Cápsula:</span>
            <span>${translateCapsuleType(formula.capsule_type)}</span>
          </div>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th rowspan="2" style="text-align:left;width:200px;">INGREDIENTE</th>
              <th rowspan="2">CANT. POR UNIDAD</th>
              <th colspan="4">CANTIDAD DE PRODUCCIÓN (unidades)</th>
            </tr>
            <tr>
              ${prodQtys.map(q => `<th>${q.toLocaleString()}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${ingredients.map(ing => {
              const c = costMap[ing.ingredient_name] || {};
              const totalPerContainer = parseFloat(c.total_quantity || 0);
              const unit = (ing.unit || c.unit || '').toUpperCase();
              const qtyLabel = `${parseFloat(ing.quantity_per_unit || 0).toFixed(3)} ${unit.toLowerCase()}`;
              return `
              <tr>
                <td>
                  ${ing.ingredient_name}
                  ${ing.base_unit ? `<div class="base-unit">base: ${ing.base_unit.toLowerCase()}</div>` : ''}
                </td>
                <td class="text-right">${qtyLabel}</td>
                ${prodQtys.map(q => `<td class="text-right">${fmtProdQty(totalPerContainer * q, unit)}</td>`).join('')}
              </tr>`;
            }).join('')}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2"><strong>TOTAL INGREDIENTES</strong></td>
              ${prodQtys.map(() => `<td></td>`).join('')}
            </tr>
          </tfoot>
        </table>

        <div class="costs-section">
          <div class="costs-title">RESUMEN DE COSTOS (por 1 unidad de producción)</div>
          <div class="cost-row">
            <span>Subtotal Ingredientes:</span>
            <span>$${parseFloat(calculo.total_ingredient_cost || 0).toFixed(2)}</span>
          </div>
          ${parseFloat(calculo.packaging_cost || 0) > 0 ? `
          <div class="cost-row">
            <span>Empaque:</span>
            <span>$${parseFloat(calculo.packaging_cost || 0).toFixed(2)}</span>
          </div>` : ''}
          ${parseFloat(calculo.overhead_cost || 0) > 0 ? `
          <div class="cost-row">
            <span>Gastos Generales:</span>
            <span>$${parseFloat(calculo.overhead_cost || 0).toFixed(2)}</span>
          </div>` : ''}
          <div class="cost-row grand-total">
            <span>COSTO TOTAL:</span>
            <span>$${parseFloat(calculo.total_cost || 0).toFixed(2)}</span>
          </div>
          <div class="cost-row">
            <span>Costo por Unidad:</span>
            <span>$${parseFloat(calculo.cost_per_unit || 0).toFixed(2)}</span>
          </div>
        </div>

        ${formula.notes ? `
        <div style="margin-top:14px;padding:10px;border:1px solid #ddd;border-radius:4px;">
          <div class="label" style="margin-bottom:4px;">Notas:</div>
          <div>${formula.notes}</div>
        </div>` : ''}

        <div class="footer">
          <div>Generado con Betel Natural &nbsp;|&nbsp; Solo para uso interno</div>
        </div>

      </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open('text/html', 'replace');
    iframeDoc.write(tirillaHTML);
    iframeDoc.close();

    iframe.onload = function () {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 200);
    };
  };

  const translateFormulaType = (type) => {
    const tipos = {
      'BETEL': 'Betel Natural',
      'NATURALANDLAB': 'Natural & Lab',
      'COTIZACIONES': 'Cotizaciones',
      'EXCLUSIVE': 'Exclusiva'
    };
    return tipos[type] || type;
  };

  const translateContainerType = (type) => {
    const tipos = {
      'CAPSULES': 'CÁPSULAS',
      'POWDER': 'POLVO',
      'LIQUID': 'LÍQUIDO',
      'TABLET': 'TABLETA',
      'CREAM': 'CREMA',
      'OIL': 'ACEITE'
    };
    return tipos[type] || type;
  };

  const translateCapsuleType = (type) => {
    const tipos = {
      'SIZE_00': 'Tamaño 00',
      'SIZE_0': 'Tamaño 0',
      'SIZE_1': 'Tamaño 1',
      'SIZE_2': 'Tamaño 2',
      'SIZE_3': 'Tamaño 3',
      'VEGGIE': 'Vegetariana',
      'GELATIN': 'Gelatina'
    };
    return tipos[type] || type;
  };

  const translateUnit = (unit) => {
    const unidades = {
      'MG': 'mg',
      'G': 'g',
      'KG': 'kg',
      'ML': 'ml',
      'L': 'l',
      'UNIT': 'unidad',
      'UNITS': 'unidades'
    };
    return unidades[unit] || unit;
  };

  // ========== HELPERS NATURALANDLAB ==========

  // Construir grupos de clientes desde productos NATURALANDLAB
  const getNaturalAndLabClientGroups = () => {
    const map = {};
    naturalandlabProducts.forEach(product => {
      const cid = product.naturalandlab_client_id;
      const cname = product.naturalandlab_client_name || `Cliente #${cid}`;
      if (!map[cid]) {
        map[cid] = { clientId: cid, clientName: cname, products: [] };
      }
      map[cid].products.push(product);
    });
    return Object.values(map).sort((a, b) => a.clientName.localeCompare(b.clientName));
  };

  // Obtener fórmulas del cliente seleccionado
  const getFormulasForClient = (clientGroup, formulasSource = null) => {
    // Usar formulasSource si se proporciona (para búsquedas), sino usar todasFormulaciones
    const sourceFormulas = formulasSource || todasFormulaciones;

    const productIds = new Set(clientGroup.products.map(p => p.id));
    const formulas = sourceFormulas.filter(
      f => f.departamento === 'NATURALANDLAB' && f.resultProductId && productIds.has(f.resultProductId)
    );
    // Si no hay match por resultProductId, mostrar por company_name como fallback
    if (formulas.length === 0) {
      return sourceFormulas.filter(
        f => f.departamento === 'NATURALANDLAB' &&
             f.compania && f.compania.toLowerCase() === clientGroup.clientName.toLowerCase()
      );
    }
    return formulas;
  };

  // Agrupar fórmulas filtradas por cliente (para resultados de búsqueda)
  const getClientGroupsFromFormulas = (formulas) => {
    const clientMap = {};

    formulas.forEach(formula => {
      // Buscar el producto asociado a esta fórmula
      const product = naturalandlabProducts.find(p => p.id === formula.resultProductId);

      if (product && product.naturalandlab_client_id) {
        const clientId = product.naturalandlab_client_id;
        const clientName = product.naturalandlab_client_name || `Cliente #${clientId}`;

        if (!clientMap[clientId]) {
          clientMap[clientId] = {
            clientId: clientId,
            clientName: clientName,
            products: [],
            formulas: []
          };
        }

        // Agregar producto si no existe
        if (!clientMap[clientId].products.find(p => p.id === product.id)) {
          clientMap[clientId].products.push(product);
        }

        // Agregar fórmula
        if (!clientMap[clientId].formulas.find(f => f.id === formula.id)) {
          clientMap[clientId].formulas.push(formula);
        }
      }
    });

    return Object.values(clientMap).sort((a, b) => a.clientName.localeCompare(b.clientName));
  };

  const getThumbnailType = (departamento) => {
    switch (departamento) {
      case 'BETEL':
        return 'blue-dark-gradient';
      case 'NATURALANDLAB':
        return 'green-gradient';
      case 'COTIZACIONES':
        return 'purple-gradient';
      default:
        return 'gray-gradient';
    }
  };

  const getStatusBadgeClass = (estado) => {
    switch (estado) {
      case 'activa':
        return 'status-badge status-badge--success';
      case 'inactiva':
        return 'status-badge status-badge--danger';
      default:
        return 'status-badge';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando formulaciones..." />;
  }

  return (
    <div className="formulaciones-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">{getTituloFormulaciones()}</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder={activeTab === 'naturalandlab' ? 'POR CLIENTE' : 'NOMBRE FÓRMULA'}
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

        </div>
      </div>

      {/* Filter tabs */}
      <div className="filter-tabs-container">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${activeTab === 'naturalandlab' ? 'active' : ''}`}
            onClick={() => setActiveTab('naturalandlab')}
          >
            <span className="tab-label">Fórmulas Naturaland</span>{' '}
            <span className="tab-count">{getTotalFormulaciones('naturalandlab')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'cotizaciones' ? 'active' : ''}`}
            onClick={() => setActiveTab('cotizaciones')}
          >
            <span className="tab-label">Cotizaciones</span>{' '}
            <span className="tab-count">{getTotalFormulaciones('cotizaciones')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'betel' ? 'active' : ''}`}
            onClick={() => setActiveTab('betel')}
          >
            <span className="tab-label">Fórmulas Betel</span>{' '}
            <span className="tab-count">{getTotalFormulaciones('betel')}</span>
          </button>
        </div>
      </div>

      {/* Vista especial NaturalAndLab: lista de clientes o fórmulas del cliente */}
      {activeTab === 'naturalandlab' && !selectedNaturalAndLabClient && (
        <div className="naturalandlab-clients-view" style={{ marginTop: '20px' }}>
          {(() => {
            // Si hay búsqueda, agrupar por cliente las fórmulas filtradas
            const clientGroups = searchQuery
              ? getClientGroupsFromFormulas(formulaciones)
              : getNaturalAndLabClientGroups();

            if (clientGroups.length === 0) {
              return (
                <div className="empty-state">
                  <div className="empty-state-icon"><FiUser size={48} /></div>
                  <h3 className="empty-state-title">
                    {searchQuery ? 'No se encontraron resultados' : 'Sin clientes NaturalAndLab'}
                  </h3>
                  <p className="empty-state-description">
                    {searchQuery
                      ? `No se encontraron clientes o fórmulas que coincidan con "${searchQuery}"`
                      : 'Asocia clientes a productos NATURALANDLAB para verlos aquí'
                    }
                  </p>
                </div>
              );
            }

            return (
              <div className="formulas-list-container">
                {clientGroups.map((group) => {
                  // Si hay búsqueda, usar las fórmulas del grupo (ya filtradas)
                  // Si no hay búsqueda, obtener todas las fórmulas del cliente
                  const formulas = searchQuery
                    ? group.formulas
                    : getFormulasForClient(group);

                  return (
                    <div
                      key={group.clientId}
                      className="formula-list-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedNaturalAndLabClient(group)}
                    >
                      <div className="formula-info">
                        <span className="formula-name">{group.clientName}</span>
                        <span className="formula-quantity">
                          {formulas.length} fórmula{formulas.length !== 1 ? 's' : ''}
                          {' · '}
                          {group.products.length} producto{group.products.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <span style={{ color: '#7B2D8E', fontSize: '20px', paddingRight: '8px' }}>›</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Vista fórmulas del cliente seleccionado */}
      {activeTab === 'naturalandlab' && selectedNaturalAndLabClient && (
        <div style={{ marginTop: '20px' }}>
          {/* Header del cliente */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setSelectedNaturalAndLabClient(null)}
              type="button"
            >
              <FiArrowLeft /> Clientes
            </button>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#333' }}>
              {selectedNaturalAndLabClient.clientName}
            </h2>
            {searchQuery && (
              <span style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                - Resultados para "{searchQuery}"
              </span>
            )}
          </div>

          {/* Lista de fórmulas del cliente */}
          {(() => {
            // Si hay búsqueda y el grupo tiene fórmulas pre-filtradas, usarlas
            // Si no, obtener todas las fórmulas del cliente (o filtradas si hay búsqueda)
            const clientFormulas = searchQuery && selectedNaturalAndLabClient.formulas
              ? selectedNaturalAndLabClient.formulas
              : getFormulasForClient(selectedNaturalAndLabClient, searchQuery ? formulaciones : null);

            if (clientFormulas.length === 0) {
              return (
                <div className="empty-state">
                  <div className="empty-state-icon"><FiFileText size={48} /></div>
                  <h3 className="empty-state-title">Sin fórmulas</h3>
                  <p className="empty-state-description">
                    {searchQuery
                      ? `Este cliente no tiene fórmulas que coincidan con "${searchQuery}"`
                      : 'Este cliente no tiene fórmulas registradas aún'
                    }
                  </p>
                </div>
              );
            }

            return (
              <div className="formulas-list-container">
                {clientFormulas.map((formulacion) => (
                  <div
                    key={formulacion.id}
                    className="formula-list-item"
                    onClick={() => navigate(`/system/formulaciones/${formulacion.id}`)}
                  >
                    <div className="formula-info">
                      <span className="formula-name">{formulacion.nombreProducto}</span>
                      <span className="formula-quantity">{formulacion.tipoEnvase || 'Sin envase'}</span>
                    </div>
                    <button
                      className="formula-print-btn"
                      onClick={(e) => { e.stopPropagation(); handleImprimir(formulacion); }}
                      title="Imprimir fórmula"
                    >
                      <FiPrinter />
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Contenido normal (SOLO para pestañas que NO son naturalandlab) */}
      {activeTab !== 'naturalandlab' && formulaciones.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiFileText size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron formulaciones' : 'No hay formulaciones registradas'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron formulaciones que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primera formulación al sistema'
            }
          </p>
          {searchQuery && (
            <p>
              <button
                className="btn btn--ghost"
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : null}

      {/* Vista de tabla (SOLO para pestañas betel/cotizaciones, NO para naturalandlab) */}
      {activeTab !== 'naturalandlab' && formulaciones.length > 0 && (
        <div className="formulas-list-container" style={{ marginTop: '20px' }}>
          {formulaciones.map((formulacion) => (
            <div
              key={formulacion.id}
              className="formula-list-item"
              onClick={() => navigate(`/system/formulaciones/${formulacion.id}`)}
            >
              <div className="formula-info">
                <span className="formula-name">{formulacion.nombreProducto}</span>
                <span className="formula-quantity">{formulacion.tipoEnvase || 'Sin envase'}</span>
              </div>
              <button
                className="formula-print-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleImprimir(formulacion);
                }}
                title="Imprimir fórmula"
              >
                <FiPrinter />
              </button>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default Formulaciones;
